import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Info, Step, Warning } from '../../components/DocsComponents'
import planAllowances from '../../../helpers/planAllowances.json'

// Figures come from the plan catalogue mirror, never typed in here.
const { core, premium, enterprise } = planAllowances.plans

const AddingEmployees = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">Two ways to add staff</Typography>
      <Typography>
        Employees can be added manually from{' '}
        <DocLink text="Employees > New" url="/employees/new" />, or created
        automatically from an approved application submitted through your
        organisation's recruitment portal.
      </Typography>
      <Info title="Who can do this?">
        Creating and editing employee records requires the{' '}
        <strong>Personnel</strong> team on your employee account. Site
        administrators always have access.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Minimum information</Typography>
      <Typography>
        To create a new employee record you must provide a first name, last
        name, email address, and at least one <strong>base location</strong>.
        If your organisation has exactly one active base, it is applied
        automatically; with more than one, you must pick the base(s) — the
        record is refused otherwise, so no account ever starts without a base.
      </Typography>
      <Typography>
        The email address must not already belong to another account. That
        includes active employees, leavers, current applicants, PTS patients,
        hospital users, and event organiser accounts.
      </Typography>
    </Stack>

    <Stack gap={0}>
      <Typography variant="title3">Manual employee creation</Typography>
      <Step number={1} title="Open Employees">
        Go to <DocLink text="Employees" url="/employees" /> in the People
        section of the sidebar and select the new employee action.
      </Step>
      <Step number={2} title="Create the record">
        Enter the required personal details and save. You'll be taken straight
        to the new employee's profile.
      </Step>
      <Step number={3} title="Complete the profile">
        Add contact information, address, next of kin, professional
        qualifications, controlled drugs information, driving licence details,
        and user account settings as required by your organisation.
      </Step>
      <Step number={4} title="Invite the employee">
        From the profile, send the welcome email so the employee can set their
        own password and sign in.
      </Step>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Creating employees from applications
      </Typography>
      <Typography>
        Applications are managed from{' '}
        <DocLink text="Employees > Applications" url="/employees/applications" />
        . When you accept an application, it is converted into an employee
        record — documents, qualifications, DBS and driving details carry
        across, so nothing needs re-entering. See{' '}
        <DocLink
          docsPage
          url="/employees-hr/recruitment-applications"
          text="Recruitment & Applications"
        />{' '}
        for your application link and the full process from sign-up to
        onboarding.
      </Typography>
      <Info>
        Employee applications require a <strong>Premium</strong> (tier 2) or
        higher subscription, and reviewing them requires the{' '}
        <strong>Personnel</strong> team.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Subscription employee limits</Typography>
      <Typography>
        Each subscription tier includes a set number of active employees: Core
        includes up to {core.allowances.employees}, Premium up to{' '}
        {premium.allowances.employees}, and Enterprise up to{' '}
        {enterprise.allowances.employees}. Applicants, leavers, and archived
        staff do not count towards the limit. Additional employees beyond your
        included limit are billed at £2 per employee per month.
      </Typography>
      <Typography>
        Extra capacity on top of the plan can be <strong>purchased</strong>{' '}
        seats or <strong>complimentary</strong> seats granted by AmbuNet — the
        Billing page's usage panel shows your effective limit broken down (for
        example "200 included with your plan, plus 10 purchased and 5
        complimentary").
      </Typography>
      <Typography>
        Once the limit is reached, creating a new employee is blocked until
        capacity is increased or inactive records are marked as leavers.
      </Typography>
      <Info title="Managing extra employees">
        A site administrator can add or reduce additional employee capacity
        from <DocLink text="Configuration > Billing" url="/billing" />. Changes
        are charged pro rata for the rest of the current billing period and
        then continue monthly.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">After the employee is added</Typography>
      <Typography>
        Review the employee profile before they are used operationally:
      </Typography>
      <ul style={{ marginTop: 0 }}>
        <li>Assign the correct teams and permissions.</li>
        <li>Confirm employment status.</li>
        <li>Add or verify qualifications.</li>
        <li>Check DBS and driving licence details where needed.</li>
        <li>Review mandatory training requirements.</li>
        <li>
          Confirm whether they should appear in shifts, events, PTS, CAD, or
          other operational workflows.
        </li>
      </ul>
      <Warning title="Operational safety">
        Keep employee records accurate. AmbuNet uses employee data to support
        access control, training compliance, shift eligibility, deployment, and
        audit history.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Converting a subcontractor to an employee
      </Typography>
      <Typography>
        If an external clinician already works for you as a subcontractor and
        joins your workforce, you don't need to create them from scratch —
        convert their account from the Subcontractors page. The same user
        account is kept (their login, shift history, and records move with
        them) and the subcontractor record is archived. See{' '}
        <DocLink
          docsPage
          url="/employees-hr/subcontractors"
          text="Subcontractors"
        />
        .
      </Typography>
    </Stack>
  </Stack>
)

export default AddingEmployees
